import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { cn } from "@/lib/utils";

export interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  onRowClick?: (row: TData) => void;
  selectedRowId?: (row: TData) => boolean;
  className?: string;
}

export function DataTable<TData>({
  data,
  columns,
  onRowClick,
  selectedRowId,
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={cn("overflow-x-auto rounded-5 border border-line bg-surface", className)}>
      <table className="w-full min-w-[640px] text-[13px] text-ink-700">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-line bg-surface-1">
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className="px-4 py-2 text-left font-mono text-[9.5px] font-medium uppercase tracking-[.08em] text-ink-500"
                >
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isSelected = selectedRowId?.(row.original);
            return (
              <tr
                key={row.id}
                data-testid="data-row"
                onClick={() => onRowClick?.(row.original)}
                className={cn(
                  "border-b border-line last:border-0",
                  "hover:bg-[color-mix(in_oklab,var(--ink-900)_2.5%,transparent)]",
                  isSelected && "bg-p-50 shadow-[inset_3px_0_0_var(--p-600)]",
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-2.5">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface TableCellNumberProps {
  children: React.ReactNode;
  className?: string;
}

export function TableCellNumber({ children, className }: TableCellNumberProps) {
  return <span className={cn("font-mono tabular-nums text-ink-700", className)}>{children}</span>;
}

export interface TableCellIdProps {
  prefix: string;
  value: string | number;
  primary?: boolean;
}

export function TableCellId({ prefix, value, primary = false }: TableCellIdProps) {
  return (
    <span className="font-mono tracking-tight">
      <span className="text-ink-400">{prefix}</span>
      <span className={primary ? "text-ink-900" : "text-ink-700"}>{value}</span>
    </span>
  );
}
